import { useEffect, useState } from "react";
import * as eventService from "../../services/eventService";

type Props = {
  open: boolean;
  onClose: () => void;
  evento: eventService.EventoAdmin | null;
  onUpdated: (evento: eventService.EventoAdmin) => void;
};

const DEFAULT_TIME_UTC = "20:00:00Z";

const ESTADOS = [
  { value: "PLANIFICADO", label: "Planificado" },
  { value: "ENVIADO", label: "Enviado" },
  { value: "TERMINADO", label: "Terminado" },
  { value: "CANCELADO", label: "Cancelado" },
] as const;

function toDateInputValue(iso?: string | null) {
  if (!iso) return "";
  return iso.slice(0, 10);
}

function toIsoFromDate(dateYYYYMMDD: string) {
  return `${dateYYYYMMDD}T${DEFAULT_TIME_UTC}`;
}

function normalizeEstadoEvento(estado?: string | null) {
  if (estado === "BORRADOR" || estado === "CONFIRMADO") return "PLANIFICADO";
  if (estado === "COMPLETADO") return "TERMINADO";
  return estado || "PLANIFICADO";
}

function EditEventModal({ open, onClose, evento, onUpdated }: Props) {
  const [nombre, setNombre] = useState("");
  const [slug, setSlug] = useState("");
  const [fechaPrincipalDate, setFechaPrincipalDate] = useState("");
  const [fechaLimiteRsvpDate, setFechaLimiteRsvpDate] = useState("");
  const [zonaHoraria, setZonaHoraria] = useState("America/Lima");
  const [estado, setEstado] = useState("PLANIFICADO");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !evento) return;
    setNombre(evento.nombre ?? "");
    setSlug(evento.slug ?? "");
    setFechaPrincipalDate(toDateInputValue(evento.fechaPrincipal));
    setFechaLimiteRsvpDate(toDateInputValue(evento.fechaLimiteRsvp));
    setZonaHoraria(evento.zonaHoraria || "America/Lima");
    setEstado(normalizeEstadoEvento(evento.estado));
    setErrorMsg(null);
  }, [open, evento]);

  if (!open || !evento) return null;
  const eventoId = evento.id;

  async function actualizar(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setErrorMsg(null);

    try {
      const updated = await eventService.actualizarEvento(eventoId, {
        nombre,
        slug,
        fechaPrincipal: toIsoFromDate(fechaPrincipalDate),
        fechaLimiteRsvp: toIsoFromDate(fechaLimiteRsvpDate),
        zonaHoraria,
        estado: normalizeEstadoEvento(estado),
      });

      onUpdated(updated);
      onClose();
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "Error al actualizar evento");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-black/60 backdrop-blur-[2px]"
        onClick={() => {
          if (!loading) onClose();
        }}
        aria-label="Cerrar"
      />

      <div className="relative w-full max-w-[680px] rounded-2xl border border-white/10 bg-surface-darker shadow-2xl overflow-hidden">
        <div className="px-6 py-5 border-b border-white/10 flex items-center justify-between">
          <div>
            <h3 className="text-xl font-serif text-white">Editar evento</h3>
            <p className="text-sm text-slate-400">
              Actualiza datos generales, fecha RSVP y estado del evento.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="p-2 rounded-lg text-slate-300 hover:text-white hover:bg-white/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Cerrar modal"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        <form onSubmit={actualizar} className="px-6 py-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-white">Nombre</label>
              <input
                className="w-full rounded-lg border border-white/10 bg-background-dark text-white placeholder-slate-500 focus:border-primary focus:ring-1 focus:ring-primary h-11 px-4 transition-all duration-200 disabled:opacity-70"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                required
                disabled={loading}
              />
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-white">Slug</label>
              <input
                className="w-full rounded-lg border border-white/10 bg-background-dark text-white placeholder-slate-500 focus:border-primary focus:ring-1 focus:ring-primary h-11 px-4 transition-all duration-200 disabled:opacity-70"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                required
                disabled={loading}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-white">Fecha principal</label>
              <input
                type="date"
                className="w-full rounded-lg border border-white/10 bg-background-dark text-white focus:border-primary focus:ring-1 focus:ring-primary h-11 px-4 transition-all duration-200 disabled:opacity-70"
                value={fechaPrincipalDate}
                onChange={(e) => setFechaPrincipalDate(e.target.value)}
                required
                disabled={loading}
              />
              <p className="text-xs text-slate-400">
                Se enviará como: <span className="text-slate-200">{toIsoFromDate(fechaPrincipalDate)}</span>
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-white">Fecha límite RSVP</label>
              <input
                type="date"
                className="w-full rounded-lg border border-white/10 bg-background-dark text-white focus:border-primary focus:ring-1 focus:ring-primary h-11 px-4 transition-all duration-200 disabled:opacity-70"
                value={fechaLimiteRsvpDate}
                onChange={(e) => setFechaLimiteRsvpDate(e.target.value)}
                required
                disabled={loading}
              />
              <p className="text-xs text-slate-400">
                Se enviará como: <span className="text-slate-200">{toIsoFromDate(fechaLimiteRsvpDate)}</span>
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-white">Zona horaria</label>
              <input
                className="w-full rounded-lg border border-white/10 bg-background-dark text-white placeholder-slate-500 focus:border-primary focus:ring-1 focus:ring-primary h-11 px-4 transition-all duration-200 disabled:opacity-70"
                value={zonaHoraria}
                onChange={(e) => setZonaHoraria(e.target.value)}
                placeholder="America/Lima"
                required
                disabled={loading}
              />
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-white">Estado</label>
              <select
                className="w-full rounded-lg border border-white/10 bg-background-dark text-white focus:border-primary focus:ring-1 focus:ring-primary h-11 px-4 transition-all duration-200 disabled:opacity-70"
                value={estado}
                onChange={(e) => setEstado(e.target.value)}
                required
                disabled={loading}
              >
                {ESTADOS.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {errorMsg && <p className="text-sm text-red-300">{errorMsg}</p>}

          <div className="pt-2 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 h-11 rounded-lg border border-white/10 bg-white/5 text-white hover:bg-white/10 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              Cancelar
            </button>

            <button
              type="submit"
              disabled={loading}
              className="px-5 h-11 rounded-lg bg-primary hover:bg-primary/90 text-white font-semibold transition-all shadow-lg shadow-primary/20 active:scale-95 disabled:opacity-60 disabled:cursor-wait"
            >
              <span className="inline-flex items-center justify-center gap-2">
                {loading && (
                  <span className="material-symbols-outlined text-[18px] animate-spin">progress_activity</span>
                )}
                {loading ? "Actualizando..." : "Actualizar evento"}
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditEventModal;
